import {
  Attachment,
  ButtonDropdown,
  NavItem,
  useDropzoneInput,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { ChooseAttachmentModal as ChooseAttachmentModalComponent } from '../../../attachments/elements/ChooseAttachmentModal'
import { openFileDialog } from './utils/openFileDialog'

export enum FileOriginOption {
  Device = 'device',
  Uploads = 'uploads',
}

interface ChooseFileDropdownProps {
  attachmentsIdsChosen?: string[]
  dropdownClassName?: string
  modalId?: string
  onClose?: () => void
  onFileFromUploadsSelect?: (attachment: Attachment) => void
  onFileFromDeviceSelect?: (files: File[]) => void
  onOpen?: () => void
  trigger?: ReactElement
}

const ChooseAttachmentModal = withModalConditionalRender(ChooseAttachmentModalComponent)

export const ChooseFileDropdown = ({
  attachmentsIdsChosen,
  dropdownClassName,
  modalId = ModalId.AttachmentsModal,
  onClose,
  onFileFromUploadsSelect,
  onFileFromDeviceSelect,
  onOpen,
  trigger,
}: ChooseFileDropdownProps): ReactElement => {
  const { t } = useTranslation()
  const { open: openChooseFromUploadsModal } = useModal(modalId)

  const items: NavItem<FileOriginOption>[] = useMemo(
    () => [
      {
        id: FileOriginOption.Device,
        children: t('choose_file_dropdown.device'),
        value: FileOriginOption.Device,
      },
      {
        id: FileOriginOption.Uploads,
        children: t('choose_file_dropdown.uploads'),
        value: FileOriginOption.Uploads,
      },
    ],
    [t],
  )

  const handleFileFromDeviceSelect = useCallback(
    (files: File[]) => {
      onFileFromDeviceSelect?.(files)
    },
    [onFileFromDeviceSelect],
  )

  const { inputRef, inputProps } = useDropzoneInput({ onFilesSelect: handleFileFromDeviceSelect, multiple: true })

  const handleFileFromUploadsSelect = useCallback(
    (attachment: Attachment) => {
      onFileFromUploadsSelect?.(attachment)
    },
    [onFileFromUploadsSelect],
  )

  const handleItemSelect = useCallback(
    (id: string, value: FileOriginOption) => {
      switch (value) {
        case FileOriginOption.Device:
          openFileDialog(inputRef)
          break
        case FileOriginOption.Uploads:
          openChooseFromUploadsModal()
          break
      }
    },
    [inputRef, openChooseFromUploadsModal],
  )

  return (
    <>
      <ButtonDropdown
        items={items}
        onClose={onClose}
        onOpen={onOpen}
        onSelect={handleItemSelect}
        variant="outlined"
        placement="bottom-start"
        dropdownClassName={dropdownClassName}
        dropdownWidth={215}
        triggerElement={trigger}
      >
        {t('choose_file_dropdown')}
      </ButtonDropdown>
      <input ref={inputRef} {...inputProps} />

      <ChooseAttachmentModal
        id={modalId}
        idsToSkip={attachmentsIdsChosen}
        onSelect={handleFileFromUploadsSelect}
        size="xl"
      />
    </>
  )
}
